import React, { useCallback, useContext, useMemo, useReducer } from 'react'
import { useAuth } from './useAuth'
import { setLocalStorageItem } from '../utils/localStorageUtils'
import { useSettings } from './useSettings'

export type Theme = 'light' | 'dark' | 'system'

interface IThemeContext {
  isDarkMode: boolean
  theme: Theme
  setTheme: (theme: Theme) => void
}

const ThemeContext = React.createContext<IThemeContext | undefined>(undefined)

const ThemeProvider: React.FC<{
  children: React.ReactNode
}> = (props) => {
  const [, forceUpdate] = useReducer((x) => x + 1, 0)
  const auth = useAuth()
  const userId = auth?.userId
  const userSettings = useSettings()
  const settingsTheme = userSettings.get.theme

  let theme: Theme = localStorage.theme ?? 'system'

  if (!userSettings.isPending && settingsTheme != null) {
    theme = settingsTheme
    if (localStorage.theme != theme) {
      setLocalStorageItem('theme', theme)
    }
  }
  const isDarkMode =
    theme === 'dark' ||
    (theme === 'system' &&
      globalThis.matchMedia('(prefers-color-scheme: dark)').matches)
  document.documentElement.classList.toggle('dark', isDarkMode)
  document.documentElement.dataset.colorScheme = isDarkMode ? 'dark' : 'light'
  const setTheme = useCallback(
    (theme: Theme): void => {
      if (userId != undefined) {
        userSettings.set({
          theme
        })
      }
    },
    [userId, userSettings]
  )

  React.useEffect(() => {
    const darkMode = globalThis.matchMedia('(prefers-color-scheme: dark)')
    const handler = (): void => {
      forceUpdate()
    }
    darkMode.addEventListener('change', handler)
    return () => {
      darkMode.removeEventListener('change', handler)
    }
  }, [])
  const value = useMemo(
    () => ({
      isDarkMode,
      theme,
      setTheme
    }),
    [isDarkMode, theme, setTheme]
  )

  return (
    <ThemeContext.Provider value={value}>
      {props.children}
    </ThemeContext.Provider>
  )
}

const useTheme = (): IThemeContext => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
export { ThemeProvider, useTheme, ThemeContext }
